import React, { useCallback, useState, useEffect } from "react";
import * as Scrivito from "scrivito";
import cx from "classnames";
import { UncontrolledTooltip } from "reactstrap";
import { getHomepage } from "utils";
import {
  Button,
  EllipsisWithTooltip,
  ServicePriceModal,
  PriceCalculationModal,
} from "components";
import { useTranslate } from "hooks";
import {
  useNodeId,
  useProjectId,
  useSubType,
  useWizardActions,
  useWizardTitle,
} from "objs/_ConfiguratorObjs/Wizard/stores/wizardStore";
import { useAuthStore } from "stores/authStore";
import NiceModal from "@ebay/nice-modal-react";
import { useFetchProject, useInitOpenMap } from "hooks/projectHooks";
import { TopologyTopBarIcons } from "./TopologyTopBarIcons";
import { TopologyTopBarNavigation } from "./TopologyTopBarNavigation";
import { TopologyEditableTitle } from "../../TopologyEditableTitle";
import s from "./TopologyTopBar.module.scss";

const TopologyTopBarComponent = () => {
  const translate = useTranslate();

  const { goToPreviousStep, updateWizardTitle, setWizardTitle } =
    useWizardActions();

  useEffect(() => {
    setWizardTitle();
  }, [setWizardTitle]);

  const wizardTitle = useWizardTitle();
  const nodeId = useNodeId();
  const projectId = useProjectId();
  const initOpenMap = useInitOpenMap();
  const fetchProjectData = useFetchProject({
    onSuccess: ({ data }) => {
      initOpenMap(data.project.projectNodes);
    },
  });
  const subType = useSubType();
  const currentUserTenant = useAuthStore.use.currentUser()?.tenant;

  const [editMode, setEditMode] = useState(false);

  const handleUpdateTitle = async (newTitle) => {
    await updateWizardTitle({ newTitle });
  };

  const handleStepBack = useCallback(() => {
    goToPreviousStep();
  }, [goToPreviousStep]);

  const handleShowPriceBtn = useCallback(() => {
    currentUserTenant === "DE"
      ? NiceModal.show(ServicePriceModal, { projectId, nodeId, subType })
      : NiceModal.show(PriceCalculationModal, {
          configurationOption: "commissionNotIncluded",
          projectId,
          nodeId,
          subType,
        });
  }, [currentUserTenant, nodeId, projectId, subType]);

  const handleEditMode = useCallback((val) => {
    setEditMode(val);
  }, []);

  const navigateToProjectConfigurator = useCallback(() => {
    fetchProjectData();
    Scrivito.navigateTo(getHomepage()?.get("projectConfiguratorPage"), {
      projectId,
    });
  }, [projectId, fetchProjectData]);

  return (
    <div
      className={cx(
        "position-absolute p-2 rounded-1 bg-white",
        s.topologyTopBar,
      )}
      id="topologyTopBar"
    >
      <div className="d-flex flex-wrap align-items-center py-2_5 border-bottom border-gray-300 w-100">
        <TopologyEditableTitle
          title={wizardTitle}
          handleUpdateTitle={handleUpdateTitle}
          inputClassNames="fw-bold text-blue-800 ps-2 fs-lg"
          handleEditMode={handleEditMode}
          editMode={editMode}
          className="mw-80"
        >
          <div id="wizard-title" className={cx("h5 mb-0 me-3")}>
            <EllipsisWithTooltip text={wizardTitle} />
          </div>
        </TopologyEditableTitle>

        <div className="d-flex flex-grow-1 align-items-center justify-content-between">
          <div className="d-flex flex-nowrap">
            <Button
              onClick={() => handleEditMode(true)}
              data-testid="editIcon"
              square={true}
              color="tertiary"
              iconPrefix="trox"
              icon="pencil-light"
              id="topologyEditTitle"
              iconClassName="icon-16"
              classNames="p-0 ms-1 border-0 cursor-pointer pe-2"
            />
            <UncontrolledTooltip target={`topologyEditTitle`} placement="top">
              {translate("PROJECT_SUBTITLE_RENAME")}
            </UncontrolledTooltip>
            <TopologyTopBarIcons />
          </div>

          <div className="ms-auto d-flex flex-nowrap align-items-center">
            <Button
              data-testid="TopologyTopBar_show-price-btn"
              color="secondary"
              outline
              className="me-1"
              onClick={handleShowPriceBtn}
            >
              {translate("SHOW_PRICE")}
            </Button>
            <Button
              data-testid="TopologyTopBar_show-price-btn text-"
              className="me-1"
              onClick={navigateToProjectConfigurator}
            >
              {translate("PRODUCT_CONFIGURATOR_BACK_TO_PROJECT_STRUCTURE")}
            </Button>
          </div>
        </div>
      </div>

      <div className="d-flex flex-wrap justify-content-between align-items-center py-2_5 border-bottom border-gray-300">
        <div className="text-nowrap">
          <Button
            data-testid="TopologyTopBar_back-btn"
            color="secondary"
            size="sm"
            icon="chevron-left-regular"
            iconPrefix="trox"
            iconClassName="icon-14 me-1"
            className="bg-gray-100 border border-gray-300 text-gray-700 text-nowrap"
            onClick={handleStepBack}
          >
            {translate("BACK")}
          </Button>
        </div>

        <div className="text-gray-500 font-bold text18 text-center flex-grow-1 text-wrap">
          {translate("WIZARD_AUTO_SAVE_DATA")}
        </div>

        <div className="d-flex justify-content-end flex-grow-0 w-auto flex-wrap">
          <TopologyTopBarNavigation />
        </div>
      </div>
    </div>
  );
};

export const TopologyTopBar = React.memo(
  Scrivito.connect(TopologyTopBarComponent),
);
